import React, { useEffect, useState } from 'react';
import './FeedbackModal.css';
import { authFetch } from '../../lib/keycloak-api';

const RVGFeedbackModal = ({ show, onClose, rvgData, userEmail }) => {
  const [issueTypes, setIssueTypes] = useState([]);
  const [feedbackText, setFeedbackText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const maxChars = 500;

  useEffect(() => {
    if (show) {
      setIssueTypes([]);
      setFeedbackText('');
      setCharCount(0);
      setShowSuccess(false);
    }
  }, [show]);

  const handleCheckboxChange = (type) => {
    if (issueTypes.includes(type)) {
      setIssueTypes(issueTypes.filter((t) => t !== type));
    } else {
      setIssueTypes([...issueTypes, type]);
    }
  };

  const handleTextChange = (e) => {
    const text = e.target.value;
    if (text.length <= maxChars) {
      setFeedbackText(text);
      setCharCount(text.length);
    }
  };

  const handleSubmit = async () => {
    if (issueTypes.length === 0) {
      alert('Please select at least one issue type');
      return;
    }

    if (!rvgData) {
      alert('No location data available for feedback');
      return;
    }

    setIsSubmitting(true);

    try {
      const clickedLat = rvgData?.clickedLocation?.lat ?? rvgData?.location?.lat ?? null;
      const clickedLng =
        rvgData?.clickedLocation?.lng ??
        rvgData?.clickedLocation?.long ??
        rvgData?.location?.lon ??
        rvgData?.location?.long ??
        null;
      const responseLat = rvgData?.location?.lat ?? rvgData?.lat ?? null;
      const responseLng =
        rvgData?.location?.lon ??
        rvgData?.location?.long ??
        rvgData?.lng ??
        rvgData?.long ??
        null;

      const payload = {
        source: 'REVERSE_GEOCODE',
        clicked_location: {
          lat: clickedLat,
          lng: clickedLng
        },
        rvg_response: {
          entity_id: rvgData?.entity_id ?? null,
          display_text: rvgData?.display_text || '',
          full_address: rvgData?.full_address || '',
          entity_name: rvgData?.entity_name || rvgData?.display_name || rvgData?.name || '',
          entity_type: rvgData?.entity_type || rvgData?.type || '',
          type: rvgData?.type || rvgData?.entity_type || '',
          lat: responseLat,
          lng: responseLng,
          score: rvgData?.score ?? null
        },
        issue: {
          issue_types: issueTypes,
          feedback_text: feedbackText.trim() || ''
        },
        user_context: {
          email: userEmail || 'anonymous@delhivery.com',
          session_id: `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          device: 'web'
        },
        timestamp: new Date().toISOString()
      };

      const response = await authFetch('https://search-maps-pub-int.delhivery.com/v1/feedback/rvg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      setShowSuccess(true);
    } catch (error) {
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setShowSuccess(false);
    onClose();
  };

  if (!show) return null;

  return (
    <div className="feedback-modal-overlay" onClick={handleClose}>
      <div
        className={`feedback-modal ${feedbackText.length > 0 ? 'feedback-modal--filled' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {!showSuccess ? (
          <>
            <div className="feedback-header-content">
              <button className="feedback-modal-close" onClick={handleClose} aria-label="Close">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
              <h2 className="feedback-modal-title">Report an issue</h2>
            </div>

            <div className="feedback-modal-divider"></div>
            <div className="feedback-content-container">
              <div className="feedback-section feedback-section--issues">
                <label className="feedback-label">What went wrong?</label>
                <div className="feedback-checkboxes">
                  <label className="feedback-checkbox-item">
                    <div className={`feedback-checkbox ${issueTypes.includes('ADDRESS') ? 'checked' : ''}`}>
                      {issueTypes.includes('ADDRESS') && (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      )}
                    </div>
                    <input
                      type="checkbox"
                      checked={issueTypes.includes('ADDRESS')}
                      onChange={() => handleCheckboxChange('ADDRESS')}
                      style={{ display: 'none' }}
                    />
                    <span className="feedback-checkbox-label">Address Issue</span>
                  </label>

                  <label className="feedback-checkbox-item">
                    <div className={`feedback-checkbox ${issueTypes.includes('MISSING PLACE') ? 'checked' : ''}`}>
                      {issueTypes.includes('MISSING PLACE') && (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      )}
                    </div>
                    <input
                      type="checkbox"
                      checked={issueTypes.includes('MISSING PLACE')}
                      onChange={() => handleCheckboxChange('MISSING PLACE')}
                      style={{ display: 'none' }}
                    />
                    <span className="feedback-checkbox-label">Missing Place</span>
                  </label>
                </div>
              </div>

              <div className="feedback-section feedback-section--description">
                <div className="feedback-label-row">
                  <label className="feedback-label">Description</label>
                  <span className="feedback-optional">Optional</span>
                </div>
                <textarea
                  className="feedback-textarea"
                  placeholder="Share your experience with us. What's working well? What could've gone better?"
                  value={feedbackText}
                  onChange={handleTextChange}
                  rows={5}
                />
                <div className="feedback-char-count">{charCount}/{maxChars}</div>
              </div>
            </div>

            <div className="feedback-actions">
              <button
                className={`feedback-submit-btn ${issueTypes.length === 0 ? 'disabled' : ''}`}
                onClick={handleSubmit}
                disabled={issueTypes.length === 0 || isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </>
        ) : (
          <>
            <button className="feedback-modal-close" onClick={handleClose} aria-label="Close">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            <div className="feedback-success feedback-success-container">
              <div className="feedback-success-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80" fill="none">
                  <mask
                    id="mask0_1795_930"
                    style={{ maskType: 'alpha' }}
                    maskUnits="userSpaceOnUse"
                    x="0"
                    y="0"
                    width="80"
                    height="80"
                  >
                    <rect width="80" height="80" fill="#D9D9D9" />
                  </mask>
                  <g mask="url(#mask0_1795_930)">
                    <path d="M35.205 48.3075L26.885 39.9875C26.5728 39.6753 26.1903 39.5086 25.7375 39.4875C25.2842 39.4658 24.8803 39.6325 24.5258 39.9875C24.1708 40.3419 23.9933 40.735 23.9933 41.1667C23.9933 41.5983 24.1708 41.9914 24.5258 42.3458L33.3208 51.1408C33.8592 51.6792 34.4872 51.9483 35.205 51.9483C35.9228 51.9483 36.5511 51.6792 37.09 51.1408L55.3458 32.885C55.6581 32.5728 55.8247 32.1903 55.8458 31.7375C55.8675 31.2842 55.7008 30.8803 55.3458 30.5258C54.9914 30.1708 54.5983 29.9933 54.1667 29.9933C53.735 29.9933 53.3419 30.1708 52.9875 30.5258L35.205 48.3075ZM40.0108 70C35.8625 70 31.9622 69.2128 28.31 67.6383C24.6583 66.0639 21.4817 63.9272 18.78 61.2283C16.0783 58.5294 13.9397 55.3556 12.3642 51.7067C10.7881 48.0583 10 44.1597 10 40.0108C10 35.8625 10.7872 31.9622 12.3617 28.31C13.9361 24.6583 16.0728 21.4817 18.7717 18.78C21.4706 16.0783 24.6444 13.9397 28.2933 12.3642C31.9417 10.7881 35.8403 10 39.9892 10C44.1375 10 48.0378 10.7872 51.69 12.3617C55.3417 13.9361 58.5183 16.0728 61.22 18.7717C63.9217 21.4706 66.0603 24.6444 67.6358 28.2933C69.2119 31.9417 70 35.8403 70 39.9892C70 44.1375 69.2128 48.0378 67.6383 51.69C66.0639 55.3417 63.9272 58.5183 61.2283 61.22C58.5294 63.9217 55.3556 66.0603 51.7067 67.6358C48.0583 69.2119 44.1597 70 40.0108 70ZM40 66.6667C47.4444 66.6667 53.75 64.0833 58.9167 58.9167C64.0833 53.75 66.6667 47.4444 66.6667 40C66.6667 32.5556 64.0833 26.25 58.9167 21.0833C53.75 15.9167 47.4444 13.3333 40 13.3333C32.5556 13.3333 26.25 15.9167 21.0833 21.0833C15.9167 26.25 13.3333 32.5556 13.3333 40C13.3333 47.4444 15.9167 53.75 21.0833 58.9167C26.25 64.0833 32.5556 66.6667 40 66.6667Z" fill="#1C1B1F" />
                  </g>
                </svg>
              </div>
              <h2 className="feedback-success-title">Feedback submitted</h2>
              <p className="feedback-success-text">
                Thank you for your feedback.<br />
                It will help us improve Delhivery Maps.
              </p>
            </div>

            <div className="feedback-modal-divider"></div>

            <div className="feedback-actions feedback-success-actions">
              <button className="feedback-close-btn" onClick={handleClose}>
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RVGFeedbackModal;

